import React from 'react';

import { Banner, Box, Button, H1, Text } from '@betterment/samba-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Header } from './Header';
import PackageNotFoundError from './PackageNotFoundError';

import Docusign from '../../AdvisedSignup401kAgreementSignature/components/Docusign';
import { AdvisedSoloPlanInvitationQuery } from '../graphql/query.advisedSoloPlanInvitation.generated';

type SignablePackage = NonNullable<
  NonNullable<AdvisedSoloPlanInvitationQuery['advisedSoloFourOhOneKClientInvite']>['signablePackage']
>;

export const SignDocument: React.FC<{ logoUrl?: string; signablePackage?: SignablePackage | null }> = ({
  logoUrl,
  signablePackage,
}) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const handleContinue = () => {
    void navigate(`/accept_solo_401k/account_agreements?invitation_token=${params.get('invitation_token')}`);
  };

  const renderDocusignContent = () => {
    if (!signablePackage) {
      return <PackageNotFoundError />;
    }

    const { docusignSignature } = signablePackage;

    if (signablePackage.signed) {
      return (
        <Banner variant="success">
          <Banner.Content>
            <Text as="p">The plan adoption agreement is signed. Please proceed to the next step.</Text>
          </Banner.Content>
        </Banner>
      );
    }

    const errorBanner = (
      <Banner variant="critical">
        <Banner.Content>
          <Text as="p">
            Error loading signature documents.{' '}
            <Button variant="textPrimary" onClick={() => window.location.reload()}>
              Please try again
            </Button>
          </Text>
        </Banner.Content>
      </Banner>
    );

    if (!docusignSignature) {
      return errorBanner;
    }

    if (docusignSignature.status === 'ready' && docusignSignature.url) {
      return (
        <Docusign
          onSessionEnd={handleContinue}
          docusignClientId={docusignSignature.clientId}
          docusignSignatureURL={docusignSignature.url}
        />
      );
    }

    if (docusignSignature.status === 'pending') {
      return (
        <Box>
          <Text color="text.weak">Generating your documents. Please try again in a few minutes.</Text>
        </Box>
      );
    }

    return errorBanner;
  };

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header logoUrl={logoUrl} progress={66} title="Plan document" />
      <Box
        css={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'center',
          padding: 'space.400',
          paddingTop: 'space.800',
          width: '672px',
        }}
      >
        <Box css={{ pb: 'space.800' }}>
          <H1
            variant="pageTitle"
            css={{
              marginBottom: 'space.150',
              color: 'text.DEFAULT',
              paddingBottom: 'space.200',
            }}
          >
            Review and sign your solo 401(k) plan adoption and trust agreements
          </H1>
          <Text
            as="p"
            css={{
              fontSize: 'fontSize.100',
              color: 'text.DEFAULT',
              lineHeight: 'lineHeights.normal',
              textAlign: 'start',
              paddingBottom: 'space.400',
            }}
          >
            Please review and sign the solo 401(k) adoption and trust agreements, which are required to open your solo
            401(k) plans (Traditional + Roth). Additionally, consider reviewing the plan document, QDRO, and IRS opinion
            letter for additional information.
          </Text>

          {renderDocusignContent()}

          <Box css={{ mt: 'space.400' }}>
            <Button
              variant="solidPrimary"
              onClick={handleContinue}
              disabled={!signablePackage || !signablePackage.signed}
            >
              Continue
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
